export type BotGroupRole = 'authority' | 'ops' | 'family' | 'silent';

export interface BotGroup {
  id: string;
  jid: string;
  name: string;
  role: BotGroupRole;
  enabled: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBotGroup {
  jid: string;
  name: string;
  role: BotGroupRole;
  enabled?: boolean;
  notes?: string;
}

function botApiUrl(): string {
  return (import.meta.env.VITE_BOT_API_URL as string | undefined) || 'https://gg.ainaeco.uk/ano-bot';
}

function botApiHeaders(): Record<string, string> {
  const key = (import.meta.env.VITE_BOT_API_KEY as string | undefined) || '';
  return {
    'Content-Type': 'application/json',
    ...(key ? { Authorization: `Bearer ${key}` } : {}),
  };
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${botApiUrl()}${path}`, {
    ...options,
    headers: { ...botApiHeaders(), ...(options.headers as Record<string, string> || {}) },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Bot API ${options.method || 'GET'} ${path} failed (${res.status}): ${text}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function listBotGroups(): Promise<BotGroup[]> {
  return request<BotGroup[]>('/api/bot-groups');
}

export async function addBotGroup(data: CreateBotGroup): Promise<BotGroup> {
  return request<BotGroup>('/api/bot-groups', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBotGroup(id: string, data: Partial<Omit<CreateBotGroup, 'jid'>> & { enabled?: boolean }): Promise<BotGroup> {
  return request<BotGroup>(`/api/bot-groups/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteBotGroup(id: string): Promise<void> {
  return request<void>(`/api/bot-groups/${id}`, { method: 'DELETE' });
}
